import React, { useState, useEffect } from "react";
import { FileText, Send, Loader2, CheckCircle, XCircle, Edit2, Upload, Eye } from "lucide-react";
import axios from "axios";
import DoneByBadge from "./DoneByBatch";
import UploadEducationModal from "./UploadEducationalModal";
import useDocumentPreview, { isImageUrl } from "../../hooks/useDocumentPreview";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const inputCls =
  "w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-transparent transition disabled:bg-gray-50 disabled:cursor-not-allowed";

const InlineBanner = ({ msg, type }) => {
  if (!msg) return null;
  return (
    <div className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 mt-2 ${
      type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"
    }`}>
      {type === "success" ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      {msg}
    </div>
  );
};

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const EShramSection = ({
  workerId,
  isRefChecked = null,
  initialEsharmId = null,
  doneByName = null,
  doneAt = null,
  initialDocumentUrl = null,
  initialDocumentMeta = null,
  onRefresh = null,
}) => {
  const [eShramId, setEShramId] = useState(initialEsharmId || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "success" });
  const [hasEShramId, setHasEShramId] = useState(!!initialEsharmId);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [localMeta, setLocalMeta] = useState({ byName: doneByName, at: doneAt });
  
  // Document state
  const [documentUrl, setDocumentUrl] = useState(initialDocumentUrl || null);
  const [documentMeta, setDocumentMeta] = useState(initialDocumentMeta || null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);

  const { openDocumentPreview } = useDocumentPreview();

  useEffect(() => { 
    setLocalMeta({ byName: doneByName, at: doneAt }); 
  }, [doneByName, doneAt]);

  useEffect(() => {
    if (initialEsharmId) {
      setEShramId(initialEsharmId);
      setHasEShramId(true);
      setIsEditing(false);
    }
  }, [initialEsharmId]);

  // Fetch document data from the esharm object if not provided
  useEffect(() => {
    const fetchEShramData = async () => {
      if (!workerId || hasEShramId) return;
      setIsLoading(true);
      try {
        const token = localStorage.getItem("superadmintoken");
        const response = await axios.post(
          `${BASE_URL}/lenskart-admin/get-worker-reference-check`,
          { workerId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.success && response.data.data?.esharm?.UAN) {
          const esharmData = response.data.data.esharm;
          setEShramId(esharmData.UAN);
          setHasEShramId(true);
          setIsEditing(false);
          
          // Check for document in esharm data
          if (esharmData.url) {
            setDocumentUrl(esharmData.url);
            setDocumentMeta({
              uploadedAt: esharmData.uploadedAt,
              uploadedBy: esharmData.uploadedBy,
              uploadedByName: esharmData.uploadedByName || esharmData.doneByName,
            });
          }
        }
      } catch { /* silent */ }
      finally { setIsLoading(false); }
    };
    if (!hasEShramId) fetchEShramData();
  }, [workerId, hasEShramId]);

  const flash = (msg, type = "success") => {
    setMessage({ text: msg, type });
    setTimeout(() => setMessage({ text: "", type: "success" }), 3500);
  };

  const handleSubmit = async () => {
    if (!eShramId.trim()) { flash("Please enter e-Shram ID", "error"); return; }
    if (eShramId.length !== 12) { flash("e-Shram ID must be exactly 12 digits", "error"); return; }
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("superadmintoken");
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/update-esharm-id`,
        { workerId, esharmId: eShramId.trim() },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      if (response.data.success) {
        setHasEShramId(true);
        setIsEditing(false);
        setLocalMeta({ byName: response.data.doneByName || localMeta.byName, at: new Date().toISOString() });
        flash(response.data.message || "e-Shram ID saved successfully.", "success");
        if (onRefresh) onRefresh();
      } else {
        throw new Error(response.data.message || "Failed to save e-Shram ID");
      }
    } catch (err) {
      flash(err.response?.data?.message || err.message || "Failed to save e-Shram ID.", "error");
    } finally {
      setIsSubmitting(false); }
  };

  const handleEdit = () => { setIsEditing(true); setMessage({ text: "", type: "success" }); };
  const handleCancelEdit = () => {
    if (hasEShramId) { setEShramId(initialEsharmId || ""); setIsEditing(false); setMessage({ text: "", type: "success" }); }
    else setIsEditing(false);
  };

  const handleDocumentUploadSuccess = (data) => {
    setIsUploadingDoc(true);
    try {
      // The response data structure from the upload API
      // The response comes through the modal's onSuccess callback
      // data contains the response from the upload API
      
      let url = null;
      let uploadedBy = null;
      let uploadedAt = null;
      let uploadedByName = null;
      
      // Check if we have the data in the expected format
      if (data) {
        // If data contains the esharm fields directly
        if (data["esharm.url"]) {
          url = data["esharm.url"];
          uploadedBy = data["esharm.uploadedBy"];
          uploadedAt = data["esharm.uploadedAt"];
        } 
        // If data contains the nested structure from the modal
        else if (data.data) {
          const nestedData = data.data;
          if (nestedData["esharm.url"]) {
            url = nestedData["esharm.url"];
            uploadedBy = nestedData["esharm.uploadedBy"];
            uploadedAt = nestedData["esharm.uploadedAt"];
          }
        }
        // Fallback: check if data has url directly
        else if (data.url) {
          url = data.url;
          uploadedBy = data.uploadedBy;
          uploadedAt = data.uploadedAt;
        }
      }

      if (url) {
        setDocumentUrl(url);
        setDocumentMeta({
          uploadedAt: uploadedAt || new Date().toISOString(),
          uploadedBy: uploadedBy || null,
          uploadedByName: uploadedByName || uploadedBy || null,
        });
        flash("Document uploaded successfully!", "success");
        setShowUploadModal(false);
        if (onRefresh) onRefresh();
      } else {
        // Even if we can't parse the URL, close modal and refresh
        setShowUploadModal(false);
        if (onRefresh) onRefresh();
        flash("Document uploaded successfully!", "success");
      }
    } catch (error) {
      console.error("Error processing upload response:", error);
      flash("Document uploaded but could not process response.", "error");
    } finally {
      setIsUploadingDoc(false);
    }
  };

  const handleViewDocument = () => {
  if (!documentUrl) return;

  if (isImageUrl(documentUrl)) {
    // Images open natively in the browser
    window.open(documentUrl, "_blank", "noopener,noreferrer");
  } else {
    // PDFs / other docs go through the Google viewer
    const viewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(documentUrl)}`;
    window.open(viewUrl, "_blank", "noopener,noreferrer");
  }
};

  // Get the file name from URL
  const getFileNameFromUrl = (url) => {
    if (!url) return "Document";
    try {
      // Extract filename from URL
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const parts = pathname.split('/');
      let fileName = parts[parts.length - 1];
      if (fileName) {
        // Decode and clean up the filename
        fileName = decodeURIComponent(fileName);
        // Remove UUID-like prefixes (e.g., "689dee4bda6229d3e521d9d3-1787214865666-")
        const cleanName = fileName.replace(/^[a-f0-9]+-[0-9]+-[a-f0-9]+-/, '');
        // If the cleaned name is empty or same as original, just return a generic name
        if (!cleanName || cleanName === fileName) {
  if (fileName.toLowerCase().includes(".pdf")) return "E-Shram Document.pdf";
  if (isImageUrl(url)) return "E-Shram Image";
  return "E-Shram Document";
}
        return cleanName;
      }
      return "E-Shram Document";
    } catch {
      return "E-Shram Document";
    }
  };

  if (isLoading) {
    return (
      <div className="p-3 rounded-lg border border-amber-200 bg-amber-50/30">
        <div className="flex items-center gap-1.5 mb-2 text-amber-600">
          <FileText className="h-4 w-4" />
          <span className="text-xs font-semibold">e-Shram Portal</span>
        </div>
        <div className="flex justify-center items-center py-3">
          <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
          <span className="ml-1.5 text-xs text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  // ── View mode ──
  if (hasEShramId && eShramId && !isEditing) {
    return (
      <div className="p-3 rounded-lg border border-amber-200 bg-amber-50/30">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 text-amber-600">
            <FileText className="h-4 w-4" />
            <span className="text-xs font-semibold">e-Shram Portal</span>
          </div>
          <button onClick={handleEdit} className="flex items-center gap-1 px-2 py-1 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-md transition">
            <Edit2 className="h-3.5 w-3.5" /> Edit
          </button>
        </div>

        <div className="p-2 rounded-md border border-green-200 bg-green-50">
          <div className="flex items-start gap-2">
            <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs w-full">
              <p className="font-medium text-green-700">e-Shram Registered ✓</p>
              <p className="text-gray-600 mt-0.5 break-all">ID: {eShramId}</p>
              <DoneByBadge doneByName={localMeta.byName} doneAt={localMeta.at} label="Updated" />
            </div>
          </div>
        </div>

        {/* Document section */}
        <div className="mt-3 pt-3 border-t border-amber-200/50">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-600">Document</span>
            <div className="flex items-center gap-2">
              {documentUrl && (
                <button
                  onClick={handleViewDocument}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-md transition"
                >
                  <Eye className="h-3.5 w-3.5" />
                  Preview
                </button>
              )}
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center gap-1 px-2 py-1 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-md transition"
                disabled={isUploadingDoc}
              >
                {isUploadingDoc ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="h-3.5 w-3.5" />
                )}
                {documentUrl ? "Replace" : "Upload"}
              </button>
            </div>
          </div>
          
          {documentUrl ? (
            <div className="mt-1.5 p-2 rounded border border-green-200 bg-green-50/50">
              <div className="flex items-center gap-2">
                <FileText className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-xs text-gray-600 truncate block">
                    {getFileNameFromUrl(documentUrl)}
                  </span>
                  {documentMeta?.uploadedAt && (
                    <span className="text-[10px] text-gray-400 block">
                      Uploaded {formatDate(documentMeta.uploadedAt)}
                      {documentMeta?.uploadedByName && ` by ${documentMeta.uploadedByName}`}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="mt-1.5 text-xs text-gray-400">No document uploaded</p>
          )}
        </div>

        <InlineBanner msg={message.text} type={message.type} />

        {/* Upload Modal - just for document upload, no ID field */}
        <UploadEducationModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          workerId={workerId}
          documentType="eshram"
          onSuccess={handleDocumentUploadSuccess}
          othersDocumentTypes={[]}
        />
      </div>
    );
  }

  // ── Edit / Add mode ──
  return (
    <div className="p-3 rounded-lg border border-amber-200 bg-amber-50/30">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-amber-600">
          <FileText className="h-4 w-4" />
          <span className="text-xs font-semibold">{hasEShramId ? "Edit e-Shram ID" : "e-Shram Portal"}</span>
        </div>
        {hasEShramId && isEditing && (
          <button onClick={handleCancelEdit} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded-md hover:bg-gray-200 transition">Cancel</button>
        )}
      </div>

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-600 mb-0.5">e-Shram ID <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={eShramId}
            onChange={(e) => setEShramId(e.target.value.replace(/\D/g, "").slice(0, 12))}
            placeholder="Add e-Shram ID"
            className={inputCls}
            disabled={isSubmitting}
            maxLength={12}
          />
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting || !eShramId.trim()}
          className={`flex items-center gap-1 px-3 py-1.5 text-xs bg-amber-500 text-white rounded-md hover:bg-amber-600 transition font-medium shadow-sm whitespace-nowrap ${!eShramId.trim() || isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {isSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          {isSubmitting ? "Saving..." : hasEShramId ? "Update" : "Save"}
        </button>
      </div>

      {/* Document upload in edit mode */}
      <div className="mt-3 pt-3 border-t border-amber-200/50">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-600">Document (Optional)</span>
          <div className="flex items-center gap-2">
            {documentUrl && (
              <button
                onClick={handleViewDocument}
                className="flex items-center gap-1 px-2 py-1 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-md transition"
              >
                <Eye className="h-3.5 w-3.5" />
                Preview
              </button>
            )}
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-1 px-2 py-1 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-md transition"
              disabled={isUploadingDoc}
            >
              {isUploadingDoc ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="h-3.5 w-3.5" />
              )}
              {documentUrl ? "Replace" : "Upload"}
            </button>
          </div>
        </div>
        
        {documentUrl ? (
          <div className="mt-1.5 p-2 rounded border border-green-200 bg-green-50/50">
            <div className="flex items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-xs text-gray-600 truncate block">
                  {getFileNameFromUrl(documentUrl)}
                </span>
                {documentMeta?.uploadedAt && (
                  <span className="text-[10px] text-gray-400 block">
                    Uploaded {formatDate(documentMeta.uploadedAt)}
                    {documentMeta?.uploadedByName && ` by ${documentMeta.uploadedByName}`}
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <p className="mt-1.5 text-xs text-gray-400">No document uploaded</p>
        )}
      </div>

      <InlineBanner msg={message.text} type={message.type} />

      {/* Upload Modal - just for document upload, no ID field */}
      <UploadEducationModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        workerId={workerId}
        documentType="eshram"
        onSuccess={handleDocumentUploadSuccess}
        othersDocumentTypes={[]}
      />
    </div>
  );
};

export default EShramSection;